import { Viewport } from "../../../pixel-accessor";
import type { Point } from "../../../types";
import type {
  EwaReversePixelMapper,
  PartialDerivatives,
} from "../../EwaReversePixelMapper";
import type { BestFitReversePixelMapper } from "../../BestFitReversePixelMapper";
import type { ForwardPixelMapper } from "../../ForwardPixelMapper";
import { perceptibleReciprocal } from "../../../utils";
import { InvalidArgument } from "../../../exception";

/**
 * Affine matrix represented by 1-dimension array: [sx, rx, tx, ry, sy, ty].
 */
export type AffineMatrix = [number, number, number, number, number, number];

/**
 * Project coordinates using affine projection matrix.
 *
 * @param x
 * @param y
 * @param matrix
 */
export function applyAffineMatrix(
  x: number,
  y: number,
  matrix: AffineMatrix
): Point {
  return [
    matrix[0] * x + matrix[1] * y + matrix[2],
    matrix[3] * x + matrix[4] * y + matrix[5],
  ];
}

/**
 * Returns inverted affine matrix.
 *
 * @param matrix
 *
 * @see https://imagemagick.org/api/MagickCore/distort_8c_source.html#l00095 Inverting affine matrix at ImageMagick source
 */
export function invertAffineMatrix(matrix: AffineMatrix): AffineMatrix {
  /* From "Digital Image Warping" by George Wolberg, page 50 */

  const determinant = perceptibleReciprocal(
    matrix[0] * matrix[4] - matrix[1] * matrix[3]
  );

  if (!determinant) {
    throw new InvalidArgument("Given matrix can't be inverted");
  }

  return [
    determinant * matrix[4],
    determinant * -matrix[1],
    determinant * (matrix[1] * matrix[5] - matrix[2] * matrix[4]),
    determinant * -matrix[3],
    determinant * matrix[0],
    determinant * (matrix[2] * matrix[3] - matrix[0] * matrix[5]),
  ];
}

/**
 * Affine distortion.
 *
 * @see https://www.imagemagick.org/Usage/distorts/#affine Affine distortion details at ImageMagick docs
 * @see https://imagemagick.org/api/MagickCore/distort_8c_source.html#l02443 Affine distortion at ImageMagick source
 */
export class Affine
  implements
    EwaReversePixelMapper,
    BestFitReversePixelMapper,
    ForwardPixelMapper
{
  /**
   * Forward matrix.
   */
  readonly forwardMatrix: AffineMatrix;

  /**
   * @inheritDoc
   */
  readonly isConstantPartialDerivatives = true;

  /**
   * Affine constructor.
   *
   * @param matrix Reverse Affine matrix.
   */
  constructor(readonly matrix: AffineMatrix) {
    this.forwardMatrix = invertAffineMatrix(matrix);
  }

  /**
   * Creates affine distortion using affine matrix.
   *
   * @param matrix Affine projection coefficients: [sx, rx, tx, ry, sy, ty].
   * @see https://imagemagick.org/api/MagickCore/distort_8c_source.html#l00609 Generating inverted affine distortion matrix from forward affine matrix at ImageMagick source
   */
  static fromForwardMatrix(matrix: AffineMatrix): Affine {
    return new Affine(invertAffineMatrix(matrix));
  }

  /**
   * @inheritDoc
   */
  reverseMap(x: number, y: number): Point {
    return [
      this.matrix[0] * x + this.matrix[1] * y + this.matrix[2],
      this.matrix[3] * x + this.matrix[4] * y + this.matrix[5],
    ];
  }

  /**
   * @inheritDoc
   */
  getValidity(): 1 {
    return 1;
  }

  /**
   * @inheritDoc
   */
  getPartialDerivatives(): PartialDerivatives {
    return [this.matrix[0], this.matrix[1], this.matrix[3], this.matrix[4]];
  }

  /**
   * @inheritDoc
   */
  forwardMap(u: number, v: number): Point {
    return applyAffineMatrix(u, v, this.forwardMatrix);
  }

  /**
   * @inheritDoc
   */
  getBestFitViewport(viewport: Viewport): Viewport {
    const u1 = viewport.x1,
      v1 = viewport.y1,
      u2 = viewport.x2 + 1,
      v2 = viewport.y2 + 1,
      [x, y] = this.forwardMap(u1, v1),
      bestFit = new Viewport(x, y, x, y);

    (
      [
        [u2, v1],
        [u2, v2],
        [u1, v2],
      ] as Point[]
    ).forEach((apex) => bestFit.expand(...this.forwardMap(...apex)));

    bestFit.fixBounds();

    return bestFit;
  }
}
